/**
 * Captura de audio: formatos, permisos, Wake Lock y límites.
 *
 * Todo lo que toca APIs del navegador vive acá, para que la interfaz solo
 * tenga que ocuparse de la interacción y el dominio no sepa que existe un
 * micrófono.
 *
 * NUNCA se finge que una grabación ocurrió. Si algo falla, se dice qué pasó y
 * qué hacer en su lugar.
 */

/**
 * Formatos candidatos, en orden de preferencia.
 *
 * Opus en WebM es lo que produce Chrome Android y Chromium de escritorio, que
 * es toda la matriz de destino. Pero NO se fuerza: se pregunta al navegador y
 * se guarda el mimeType REAL de lo grabado, para que otro navegador con otro
 * contenedor siga funcionando.
 *
 * No se transcodifica nada en la V1.
 */
const FORMATOS_CANDIDATOS = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
  'audio/mpeg',
] as const;

export function soportaGrabacion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    Boolean(navigator.mediaDevices?.getUserMedia)
  );
}

/** El primer formato que este navegador dice soportar, o `null`. */
export function formatoPreferido(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  if (typeof MediaRecorder.isTypeSupported !== 'function') return null;
  return FORMATOS_CANDIDATOS.find((f) => MediaRecorder.isTypeSupported(f)) ?? null;
}

/** Extensión coherente con el mimeType, para la copia descargable. */
export function extensionDe(mimeType: string): string {
  const base = mimeType.split(';')[0]!.trim().toLowerCase();
  const conocidas: Record<string, string> = {
    'audio/webm': 'webm',
    'audio/ogg': 'ogg',
    'audio/mp4': 'm4a',
    'audio/mpeg': 'mp3',
    'audio/wav': 'wav',
    'audio/x-wav': 'wav',
    'audio/aac': 'aac',
    'audio/flac': 'flac',
  };
  return conocidas[base] ?? 'audio';
}

/**
 * Límite de tamaño al cargar un archivo: 50 MB.
 *
 * De dónde sale el número, en vez de inventarlo:
 *
 *  - Lo que el programa produce es diminuto. Opus mono a ~32 kbps da unos
 *    240 KB por minuto: la muestra de 2 minutos del baseline pesaría ~0,5 MB.
 *    MEDIDO en un Android real: 747 KB para 46 segundos, o sea ~1 MB por
 *    minuto y ~2 MB por muestra de dos minutos. Cuatro veces la estimación,
 *    y aun así irrelevante: las cuatro mediciones del programa entero rondan
 *    los 8 MB. El límite no cambia por esto.
 *  - El peor caso REALISTA no es una grabación de la aplicación sino un
 *    archivo traído del teléfono sin comprimir: un WAV de 5 minutos en
 *    44,1 kHz, 16 bits y estéreo pesa unos 53 MB… así que 50 MB cubre ese
 *    mismo WAV en mono (~26 MB) con holgura de sobra.
 *  - Y sigue siendo lo bastante bajo para atajar el error de verdad: soltar un
 *    vídeo o un álbum entero por equivocación.
 *
 * El objetivo no es ahorrar bytes: es que un despiste no llene la cuota de
 * IndexedDB y se lleve por delante las muestras del baseline.
 */
export const LIMITE_ARCHIVO_BYTES = 50 * 1024 * 1024;

export function formatearTamano(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Tope para el evento `stop` del grabador. Sin él, un fallo cuelga la interfaz. */
export const ESPERA_PARADA_MS = 3000;

export type ErrorAudio =
  | 'sin_soporte'
  | 'permiso_denegado'
  | 'sin_microfono'
  | 'microfono_ocupado'
  | 'fallo_grabador'
  | 'grabacion_vacia'
  | 'archivo_no_es_audio'
  | 'archivo_demasiado_grande'
  | 'desconocido';

/** Mensajes accionables. Nunca un volcado técnico. */
export const MENSAJE_ERROR: Record<ErrorAudio, string> = {
  sin_soporte:
    'Este navegador no puede grabar audio. Usá la grabadora de tu teléfono y después tocá «Cargar audio».',
  permiso_denegado:
    'No pudimos grabar directamente desde English Training Hub porque el micrófono está bloqueado. Podés usar la grabadora de tu teléfono y después tocar «Cargar audio».',
  sin_microfono:
    'No encontramos ningún micrófono disponible. Usá la grabadora de tu teléfono y después tocá «Cargar audio».',
  microfono_ocupado:
    'El micrófono lo está usando otra aplicación. Cerrá la llamada o la app que lo tenga —ChatGPT Voice, WhatsApp, la grabadora— y probá de nuevo.',
  fallo_grabador:
    'La grabación se cortó por un fallo del navegador. Lo que llevabas grabado no se guardó. Probá de nuevo, o usá la grabadora de tu teléfono y tocá «Cargar audio».',
  grabacion_vacia:
    'La grabación salió vacía, así que no se guardó nada. Probá de nuevo y hablá unos segundos antes de detenerla.',
  archivo_no_es_audio:
    'Ese archivo no parece ser audio, o este navegador no puede reproducirlo. Probá con otro, o grabá directamente acá.',
  archivo_demasiado_grande: `Ese archivo pesa más de ${formatearTamano(LIMITE_ARCHIVO_BYTES)}. Para este programa las muestras son de un par de minutos: si es más grande, probablemente no sea el archivo que buscabas.`,
  desconocido:
    'No pudimos grabar. Podés usar la grabadora de tu teléfono y después tocar «Cargar audio».',
};

/** Cómo volver a habilitar el micrófono, cuando tiene sentido explicarlo. */
export const COMO_REHABILITAR_MICROFONO =
  'Para volver a habilitarlo en Chrome: tocá el candado (o el icono de ajustes) a la izquierda de la dirección, entrá en Permisos y permití el micrófono. Después recargá la página.';

export function clasificarErrorMedia(error: unknown): ErrorAudio {
  if (typeof DOMException !== 'undefined' && error instanceof DOMException) {
    if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
      return 'permiso_denegado';
    }
    if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
      return 'sin_microfono';
    }
    // En Android es el caso MÁS frecuente: otra aplicación tiene el
    // micrófono. Sin distinguirlo, el usuario leía «no pudimos grabar» y no
    // sabía que bastaba con cerrar la otra app.
    if (error.name === 'NotReadableError' || error.name === 'AbortError') {
      return 'microfono_ocupado';
    }
  }
  const nombre = (error as { name?: string } | null)?.name;
  if (nombre === 'NotAllowedError') return 'permiso_denegado';
  if (nombre === 'NotFoundError') return 'sin_microfono';
  if (nombre === 'NotReadableError' || nombre === 'AbortError') return 'microfono_ocupado';
  return 'desconocido';
}

/** Comprueba un archivo cargado antes de tocar la base. */
/**
 * CONTENEDORES AMBIGUOS.
 *
 * WebM, Ogg y MP4 son CONTENEDORES: el mismo formato sirve para vídeo y para
 * audio solo. Android decide el MIME de un archivo por su extensión, y en su
 * tabla `.webm` está registrado como vídeo. Resultado: un archivo que la
 * propia aplicación grabó como `audio/webm` vuelve del selector declarado
 * como `video/webm`, y una comprobación por MIME lo rechaza.
 *
 * Estos tipos NO se aceptan a ciegas: se comprueban decodificándolos.
 */
const CONTENEDORES_AMBIGUOS = new Set([
  'video/webm',
  'video/ogg',
  'video/mp4',
  'video/x-matroska',
  'video/quicktime',
  'application/octet-stream',
  'application/ogg',
  '',
]);

/** Cómo hay que tratar el tipo que declara el archivo. */
export type ClaseDeArchivo = 'audio' | 'ambiguo' | 'no_audio';

export function clasificarTipoDeArchivo(tipo: string): ClaseDeArchivo {
  const base = tipo.split(';')[0]!.trim().toLowerCase();
  if (base.startsWith('audio/')) return 'audio';
  if (CONTENEDORES_AMBIGUOS.has(base)) return 'ambiguo';
  return 'no_audio';
}

/**
 * El MIME con el que se GUARDA, no el que declaró el sistema.
 *
 * Un contenedor que resultó ser audio se guarda como audio: si no, la copia
 * descargable saldría con una extensión inventada y el ciclo
 * descargar → volver a cargar dejaría de cerrar.
 */
export function normalizarMimeDeAudio(tipo: string): string {
  const base = tipo.split(';')[0]!.trim().toLowerCase();
  const equivalencias: Record<string, string> = {
    'video/webm': 'audio/webm',
    'video/ogg': 'audio/ogg',
    'application/ogg': 'audio/ogg',
    'video/mp4': 'audio/mp4',
  };
  return equivalencias[base] ?? tipo;
}

/**
 * ¿El navegador puede decodificar esto como audio?
 *
 * Es la única comprobación que responde a la pregunta que importa —¿va a
 * poder reproducirlo esta aplicación?— en vez de fiarse de una etiqueta que
 * pone el sistema de archivos. Y no es «aceptar cualquier cosa con extensión
 * .webm»: un PDF renombrado no decodifica.
 *
 * Si no responde a tiempo, la respuesta es NO. Aceptar algo que no se pudo
 * verificar dejaría una muestra muda que solo se descubre meses después, al
 * comparar checkpoints; rechazarla se descubre ahora y se puede reintentar.
 */
export function puedeDecodificarseComoAudio(archivo: Blob): Promise<boolean> {
  return new Promise((resolver) => {
    if (typeof document === 'undefined') return resolver(false);
    const url = URL.createObjectURL(archivo);
    const audio = document.createElement('audio');
    let terminado = false;

    const terminar = (puede: boolean) => {
      if (terminado) return;
      terminado = true;
      clearTimeout(temporizador);
      URL.revokeObjectURL(url);
      audio.removeAttribute('src');
      resolver(puede);
    };

    const temporizador = setTimeout(() => terminar(false), ESPERA_METADATOS_MS);
    audio.addEventListener('loadedmetadata', () => terminar(true), { once: true });
    audio.addEventListener('error', () => terminar(false), { once: true });
    audio.preload = 'metadata';
    audio.src = url;
  });
}

/**
 * Comprueba un archivo cargado ANTES de tocar la base.
 *
 * El tamaño se mira primero: es gratis, y evita ponerse a decodificar un
 * vídeo de dos gigas para acabar rechazándolo igual.
 */
export async function validarArchivoDeAudio(archivo: File): Promise<ErrorAudio | null> {
  if (archivo.size === 0) return 'grabacion_vacia';
  if (archivo.size > LIMITE_ARCHIVO_BYTES) return 'archivo_demasiado_grande';

  const clase = clasificarTipoDeArchivo(archivo.type);
  if (clase === 'audio') return null;
  if (clase === 'no_audio') return 'archivo_no_es_audio';

  // Ambiguo: que decida el navegador, que es quien va a reproducirlo.
  return (await puedeDecodificarseComoAudio(archivo)) ? null : 'archivo_no_es_audio';
}

// ── Wake Lock ──────────────────────────────────────────────────────────────

/**
 * Mantiene la pantalla encendida mientras se graba.
 *
 * Sin esto, Android apaga la pantalla y suspende `MediaRecorder`: una muestra
 * de 2 minutos sale de 40 segundos y nadie se entera hasta reproducirla.
 *
 * NO es obligatorio. Si el navegador no lo soporta, la interfaz avisa de que
 * hay que mantener la pantalla encendida a mano.
 */
export class BloqueoDePantalla {
  private centinela: WakeLockSentinel | null = null;
  private activo = false;
  private noSostuvoNunca = false;
  private avisar: (() => void) | null = null;

  private alCambiarVisibilidad = () => {
    if (!this.activo) return;

    if (document.visibilityState === 'visible') {
      // Android suelta el bloqueo al ocultarse la pestaña: se vuelve a pedir
      // si la grabación sigue en marcha.
      void this.pedir();
      return;
    }

    /**
     * OCULTARSE MIENTRAS SE GRABA ES LA PRUEBA DE QUE EL BLOQUEO NO CUMPLIÓ.
     *
     * `wakeLock.request()` puede resolver correctamente y el dispositivo
     * apagar la pantalla igual: las capas de ahorro de energía de algunos
     * fabricantes pasan por encima, y el navegador no informa de ello. Si
     * confiamos solo en esa respuesta, la aplicación afirma que la pantalla
     * está protegida mientras el usuario ve que se apaga.
     *
     * Este evento sí llega. No distingue «se apagó la pantalla» de «saliste
     * a otra aplicación», y no hace falta: las dos cosas suspenden la
     * grabación igual, y las dos merecen el mismo aviso.
     *
     * Comprobado en un Redmi con la pantalla en 15 s: bloqueo concedido,
     * pantalla apagada a los 15 segundos, ningún aviso.
     */
    this.noSostuvoNunca = true;
    this.avisar?.();
  };

  static soportado(): boolean {
    return typeof navigator !== 'undefined' && 'wakeLock' in navigator;
  }

  /** ¿La página llegó a ocultarse mientras el bloqueo debía estar activo? */
  get noSostuvo(): boolean {
    return this.noSostuvoNunca;
  }

  /**
   * @param alNoSostener se llama la primera vez que la página se oculta con
   * el bloqueo activo, para que la interfaz pueda corregir lo que afirma.
   */
  async adquirir(alNoSostener?: () => void): Promise<boolean> {
    this.activo = true;
    this.noSostuvoNunca = false;
    this.avisar = alNoSostener ?? null;
    document.addEventListener('visibilitychange', this.alCambiarVisibilidad);
    return this.pedir();
  }

  private async pedir(): Promise<boolean> {
    if (!BloqueoDePantalla.soportado()) return false;
    try {
      this.centinela = await navigator.wakeLock.request('screen');
      return true;
    } catch {
      // Puede fallar por política del navegador o porque la pestaña no está
      // visible. No es un error que deba interrumpir la grabación.
      return false;
    }
  }

  async liberar(): Promise<void> {
    this.activo = false;
    this.avisar = null;
    document.removeEventListener('visibilitychange', this.alCambiarVisibilidad);
    try {
      await this.centinela?.release();
    } catch {
      // Liberar un centinela ya liberado no es un problema.
    }
    this.centinela = null;
    // `noSostuvoNunca` NO se limpia acá: la interfaz tiene que poder avisar
    // DESPUÉS de detener que esa muestra puede estar afectada. Se limpia al
    // empezar la grabación siguiente.
  }
}

/** Lo que se dice mientras se graba, en cuanto se comprueba que no sostuvo. */
export const AVISO_PANTALLA_NO_SOSTUVO =
  'La pantalla se apagó o saliste de la aplicación. Tu dispositivo no mantuvo la pantalla encendida aunque se lo pedimos, así que esta grabación puede haberse cortado. Volvé y mantené la pantalla visible.';

/** Lo que se dice DESPUÉS de guardar una muestra que pasó por eso. */
export const AVISO_MUESTRA_QUIZA_AFECTADA =
  'Durante esta grabación la pantalla se apagó o la aplicación quedó en segundo plano. Escuchala entera antes de seguir: si falta audio, volvé a grabarla con la pantalla encendida.';

// ── Grabación ──────────────────────────────────────────────────────────────

export interface GrabacionCapturada {
  blob: Blob;
  mimeType: string;
  segundos: number;
}

/**
 * Sesión de grabación en curso.
 *
 * El tiempo se mide con timestamps, igual que el temporizador de la sesión:
 * un contador en memoria no sobrevive a que el navegador estrangule la pestaña.
 */
export class SesionDeGrabacion {
  private grabador: MediaRecorder;
  private pista: MediaStream;
  private trozos: Blob[] = [];
  private acumulados = 0;
  private corriendoDesde: number | null = null;
  private fallo = false;

  private constructor(grabador: MediaRecorder, pista: MediaStream) {
    this.grabador = grabador;
    this.pista = pista;
    this.grabador.addEventListener('dataavailable', (evento) => {
      if (evento.data.size > 0) this.trozos.push(evento.data);
    });
    // Un `MediaRecorder` puede morirse en marcha: el sistema le quita el
    // micrófono, la pestaña pierde el dispositivo, el códec falla. Sin esto,
    // detener devolvía silencio y se guardaba una muestra vacía como si nada.
    this.grabador.addEventListener('error', () => {
      this.fallo = true;
    });
  }

  static async iniciar(): Promise<SesionDeGrabacion> {
    if (!soportaGrabacion()) throw new ErrorDeAudio('sin_soporte');

    let pista: MediaStream;
    try {
      pista = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      throw new ErrorDeAudio(clasificarErrorMedia(error));
    }

    const formato = formatoPreferido();
    const grabador = new MediaRecorder(pista, formato ? { mimeType: formato } : undefined);
    const sesion = new SesionDeGrabacion(grabador, pista);

    grabador.start();
    sesion.corriendoDesde = Date.now();
    return sesion;
  }

  get estado(): 'grabando' | 'pausada' | 'detenida' {
    if (this.grabador.state === 'recording') return 'grabando';
    if (this.grabador.state === 'paused') return 'pausada';
    return 'detenida';
  }

  segundos(ahora: number = Date.now()): number {
    const enCurso = this.corriendoDesde ? (ahora - this.corriendoDesde) / 1000 : 0;
    return Math.floor(this.acumulados + enCurso);
  }

  pausar(): void {
    if (this.grabador.state !== 'recording') return;
    if (this.corriendoDesde) {
      this.acumulados += (Date.now() - this.corriendoDesde) / 1000;
      this.corriendoDesde = null;
    }
    this.grabador.pause();
  }

  continuar(): void {
    if (this.grabador.state !== 'paused') return;
    this.corriendoDesde = Date.now();
    this.grabador.resume();
  }

  /** Detiene y devuelve el audio. Lanza si no salió nada. */
  async detener(): Promise<GrabacionCapturada> {
    if (this.corriendoDesde) {
      this.acumulados += (Date.now() - this.corriendoDesde) / 1000;
      this.corriendoDesde = null;
    }
    const segundos = Math.max(0, Math.floor(this.acumulados));

    // Esperar el evento `stop` SIN quedarse colgado. Un grabador roto puede
    // no emitirlo nunca, y entonces detener la grabación no volvería jamás:
    // el usuario se queda con el botón girando y sin forma de guardar. Con
    // el tope se sigue adelante y se usa lo que haya en los trozos.
    await new Promise<void>((resolver) => {
      if (this.grabador.state === 'inactive') return resolver();
      const tope = setTimeout(() => {
        this.fallo = true;
        resolver();
      }, ESPERA_PARADA_MS);
      this.grabador.addEventListener(
        'stop',
        () => {
          clearTimeout(tope);
          resolver();
        },
        { once: true },
      );
      try {
        this.grabador.stop();
      } catch {
        // Ya estaba parándose o el estado es inválido: seguimos igual.
        clearTimeout(tope);
        resolver();
      }
    });

    this.pista.getTracks().forEach((t) => t.stop());

    if (this.fallo) throw new ErrorDeAudio('fallo_grabador');

    const mimeType = this.grabador.mimeType || formatoPreferido() || 'audio/webm';
    const blob = new Blob(this.trozos, { type: mimeType });
    if (blob.size === 0) throw new ErrorDeAudio('grabacion_vacia');

    return { blob, mimeType, segundos };
  }

  /** Suelta el micrófono sin guardar nada. */
  cancelar(): void {
    try {
      if (this.grabador.state !== 'inactive') this.grabador.stop();
    } catch {
      // Ya estaba detenido.
    }
    this.pista.getTracks().forEach((t) => t.stop());
    this.trozos = [];
  }
}

export class ErrorDeAudio extends Error {
  constructor(public readonly tipo: ErrorAudio) {
    super(MENSAJE_ERROR[tipo]);
    this.name = 'ErrorDeAudio';
  }
}

/**
 * Duración real de un archivo de audio, leída por el navegador.
 *
 * Un archivo cargado no trae su duración en los metadatos del `File`; hay que
 * preguntársela a un elemento `<audio>`. Si no se puede averiguar, se devuelve
 * 0 en vez de inventar un número.
 */
export const ESPERA_METADATOS_MS = 3000;

export function duracionDeArchivo(archivo: Blob): Promise<number> {
  return new Promise((resolver) => {
    const url = URL.createObjectURL(archivo);
    const audio = document.createElement('audio');
    let terminado = false;

    const terminar = (segundos: number) => {
      if (terminado) return;
      terminado = true;
      clearTimeout(temporizador);
      URL.revokeObjectURL(url);
      resolver(segundos);
    };

    /**
     * Límite de espera.
     *
     * Un archivo corrupto o con un códec que el navegador no entiende puede no
     * disparar NI `loadedmetadata` NI `error`: la promesa quedaría colgada para
     * siempre y el audio nunca se guardaría, sin que el usuario viera nada.
     * Mejor guardar con duración desconocida que perder la muestra.
     */
    const temporizador = setTimeout(() => terminar(0), ESPERA_METADATOS_MS);

    audio.addEventListener('loadedmetadata', () => {
      const d = audio.duration;
      terminar(Number.isFinite(d) && d > 0 ? Math.round(d) : 0);
    });
    audio.addEventListener('error', () => terminar(0));
    audio.src = url;
  });
}
